import {APICall} from '../../../../api/api';
import {getSSPLogo} from '../../../helpers/getSSPLogo';
import {toAnalyticsPeriod} from './dataHelpers';

/**
 * Helpers for the analytics change-log panel: fetching, value formatting, search filtering,
 * grouping same-day changes by ad unit, and resolving the report selection for an ad unit.
 */

/** Fetch the ad-setup change history for the current selection/period. */
export const fetchPanelChangesBySelection = ({selection, period, realtimeMinutes, token, signal}) => {
  const periodForChanges = toAnalyticsPeriod(period, realtimeMinutes);

  return APICall({
    endpoint: 'getPanelChangesBySelection',
    options: {
      signal,
      method: 'POST',
      body: JSON.stringify({
        selection,
        period: periodForChanges,
      }),
      headers: {
        Authorization: `Bearer ${token}`,
        'Cache-Control': 'max-age=0, no-cache',
        'Content-Type': 'application/json',
      },
    },
  });
};

export const formatChangeValue = value => {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
};

export const isEnabledToggleChange = change =>
  String(change.attName || change.entityColumn || '').toLowerCase() === 'enabled';

export const readToggleState = value => {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase();
  if (normalized === '1' || normalized === 'true') return 'enabled';
  if (normalized === '0' || normalized === 'false') return 'disabled';
  return null;
};

export const formatToggleLabel = state => {
  if (!state) return null;
  return state.charAt(0).toUpperCase() + state.slice(1);
};

export const getToggleTransitionLabels = change => {
  const oldState = readToggleState(change.oldValue);
  const newState = readToggleState(change.entityValue);
  return {
    oldValue: formatToggleLabel(oldState) || '—',
    newValue: formatToggleLabel(newState) || '—',
    valueTone: newState === 'enabled' ? 'enabled' : newState === 'disabled' ? 'disabled' : 'default',
  };
};

/** Props for PanelChangeRow in the All changes list (matches summary toggle styling). */
export const isSspAttributeGroup = value => value === 1 || value === '1' || value === true;

export const resolvePanelChangeSspLogo = (record, attributeGroups) => {
  if (!record || !attributeGroups) return null;

  const tryLogo = candidate => {
    if (candidate == null || candidate === '') return null;
    return getSSPLogo(String(candidate), attributeGroups);
  };

  if (isSspAttributeGroup(record.attGroupIsSsp)) {
    return tryLogo(record.attGroupCode) || tryLogo(record.attGroupName);
  }

  if (isEnabledToggleChange(record) || record.isEnabledToggle) {
    return null;
  }

  return (
    tryLogo(record.attName) || tryLogo(record.attGroupCode) || tryLogo(record.attGroupName)
  );
};

/** Primary line is SSP / group; secondary is the setting name when both are shown. */
export const getPanelChangeTitleLines = (record, attributeGroups) => {
  const groupName = record.attGroupName || null;
  const attName = record.attName || record.entityColumn || null;
  const sspLogo = resolvePanelChangeSspLogo(record, attributeGroups);

  if (isEnabledToggleChange(record) || record.isEnabledToggle) {
    return {
      primaryLine: groupName || attName || 'Feature',
      secondaryLine: null,
      sspLogo,
    };
  }

  if (!groupName || !attName || groupName === attName) {
    return {
      primaryLine: attName || groupName || 'Attribute',
      secondaryLine: null,
      sspLogo,
    };
  }

  if (isSspAttributeGroup(record.attGroupIsSsp)) {
    return {primaryLine: groupName, secondaryLine: attName, sspLogo};
  }

  const tryLogo = candidate => {
    if (!attributeGroups || candidate == null || candidate === '') return null;
    return getSSPLogo(String(candidate), attributeGroups);
  };

  const logoFromAttName = tryLogo(attName);
  const logoFromGroup = tryLogo(groupName);

  if (logoFromAttName && !logoFromGroup) {
    return {primaryLine: attName, secondaryLine: groupName, sspLogo};
  }

  return {primaryLine: groupName, secondaryLine: attName, sspLogo};
};

export const isTagLevelChange = change =>
  String(change?.entity || '').toLowerCase() === 'tag_attribute';

export const getPanelChangeRowDisplay = (change, attributeGroups) => {
  if (isEnabledToggleChange(change)) {
    const toggle = getToggleTransitionLabels(change);
    const title = getPanelChangeTitleLines(change, attributeGroups);
    return {
      ...title,
      isTagLevel: isTagLevelChange(change),
      oldValue: toggle.oldValue,
      newValue: toggle.newValue,
      valueTone: toggle.valueTone,
      singleValue: true,
      showValues: true,
    };
  }

  const title = getPanelChangeTitleLines(change, attributeGroups);

  return {
    ...title,
    isTagLevel: isTagLevelChange(change),
    oldValue: formatChangeValue(change.oldValue),
    newValue: formatChangeValue(change.entityValue),
    valueTone: 'default',
    singleValue: false,
    showValues: true,
  };
};

const getActorType = change => change?.actorType || (change?.client === 1 ? 'client' : 'workbench');

const getActorLabel = change =>
  change?.actorUsername ||
  change?.username ||
  (getActorType(change) === 'client' ? 'Client' : 'Unknown');

export const getPanelChangeActorKey = change => `${getActorType(change)}:${getActorLabel(change)}`;

export const normalizePanelChangeActor = change => {
  const actorType = getActorType(change);
  const label = getActorLabel(change);
  const avatarId = change?.actorAvatarId ?? change?.userId ?? null;
  const avatarUrl =
    actorType === 'workbench' && avatarId != null && Number(avatarId) !== 33
      ? `https://wb.setupad.com/managers/${avatarId}.jpeg`
      : null;

  return {
    actorType,
    label,
    avatarUrl,
    key: `${actorType}:${label}`,
    userId: actorType === 'client' ? change?.userId ?? null : null,
  };
};

const isHiddenClientActor = (actor, auth) =>
  auth &&
  !auth.wb_auth &&
  actor.actorType === 'client' &&
  Number(actor.userId) === Number(auth.fos_user_id);

const toPanelChangeActors = actors =>
  actors.map(actor => (actor?.label ? actor : normalizePanelChangeActor(actor)));

const filterPanelChangeActorsForViewer = (actors, auth) =>
  !actors.length || auth?.wb_auth ? actors : actors.filter(actor => !isHiddenClientActor(actor, auth));

export const resolvePanelChangeActors = (actors, auth, show = true) => {
  if (!show || !actors?.length) return null;
  const visible = filterPanelChangeActorsForViewer(toPanelChangeActors(actors), auth);
  return visible.length ? visible : null;
};

export const collectPanelChangeActors = changes => {
  const byKey = new Map();
  for (const change of changes) {
    const key = getPanelChangeActorKey(change);
    if (!byKey.has(key)) byKey.set(key, change);
  }
  return Array.from(byKey.values())
    .map(normalizePanelChangeActor)
    .sort((a, b) => {
      if (a.actorType !== b.actorType) return a.actorType === 'workbench' ? -1 : 1;
      return a.label.localeCompare(b.label);
    });
};

export const buildPanelChangeActorOptions = (changes, auth) => [
  {key: 'all', label: 'Everyone', actorType: 'all', avatarUrl: null},
  ...filterPanelChangeActorsForViewer(collectPanelChangeActors(dedupePanelChanges(changes)), auth),
];

export const filterPanelChangesByActor = (changes, actorKey) => {
  if (!actorKey || actorKey === 'all') return changes;
  return changes.filter(change => getPanelChangeActorKey(change) === actorKey);
};

const buildSearchHaystack = change =>
  [
    change.attGroupName,
    change.entity,
    change.adName,
    change.attName,
    change.entityColumn,
    change.oldValue,
    change.entityValue,
    change.actorUsername,
    change.username,
  ]
    .filter(value => value !== null && value !== undefined && value !== '')
    .join(' ')
    .toLowerCase();

/** Tag-level logs can fan out to many ad units — count and list each log id once. */
export const dedupePanelChanges = changes => {
  if (!changes?.length) return [];
  const byId = new Map();
  for (const change of changes) {
    if (change?.id != null && !byId.has(change.id)) {
      byId.set(change.id, change);
    }
  }
  return Array.from(byId.values());
};

/** Case-insensitive filter across the visible change fields. */
export const filterPanelChanges = (changes, search) => {
  const query = (search || '').trim().toLowerCase();
  if (!query) return changes;
  return changes.filter(change => buildSearchHaystack(change).includes(query));
};

/**
 * Group same-day changes by ad unit so multiple edits to one unit are shown together.
 * Preserves the incoming order (backend returns newest first).
 */
export const groupChangesByAdUnit = changes => {
  const groups = [];
  const index = new Map();

  for (const change of changes) {
    const key =
      change.adunitId != null ? `id:${change.adunitId}` : `name:${change.adName || 'unknown'}`;
    let group = index.get(key);
    if (!group) {
      group = {
        key,
        adunitId: change.adunitId ?? null,
        adName: change.adName || null,
        domainId: change.domainId ?? null,
        publisherId: change.publisherId ?? null,
        changes: [],
      };
      index.set(key, group);
      groups.push(group);
    }
    group.changes.push(change);
  }

  return groups;
};

/**
 * Resolve a selection for an ad unit straight from the live account menu, so the result
 * matches the placement dropdown exactly (publisher/domain/placement ids + label).
 */
const resolveAdUnitSelectionFromMenu = (auth, adunitId) => {
  const domains = auth?.menu?.menu?.[1]?.children;
  if (!Array.isArray(domains)) return null;

  for (const domainNode of domains) {
    if (!Array.isArray(domainNode?.children)) continue;
    const match = domainNode.children.find(
      placementNode => String(placementNode?.path) === String(adunitId)
    );
    if (match) {
      const label = [domainNode.data?.menu?.alias, match.data?.menu?.title]
        .filter(Boolean)
        .join(' ')
        .trim();
      return {
        value: {
          publisher: domainNode.path?.[1] ?? null,
          domain: domainNode.path?.[2] ?? null,
          placement: match.path,
        },
        label: label || `Ad unit ${adunitId}`,
      };
    }
  }
  return null;
};

/**
 * Build the selection used to drill the report into a single ad unit. Prefers the live
 * account menu, then falls back to backend-provided ids and the current selection.
 * Returns `null` when the placement cannot be resolved.
 */
export const buildAdUnitSelection = (group, {auth, currentSelection} = {}) => {
  if (!group?.adunitId) return null;

  const resolved = resolveAdUnitSelectionFromMenu(auth, group.adunitId);
  if (resolved?.value?.publisher != null && resolved?.value?.domain != null) {
    return resolved;
  }

  const publisher = group.publisherId ?? currentSelection?.value?.publisher ?? null;
  const domain = group.domainId ?? currentSelection?.value?.domain ?? null;
  if (publisher == null || domain == null) return null;

  return {
    value: {publisher, domain, placement: group.adunitId},
    label: group.adName || `Ad unit ${group.adunitId}`,
  };
};

/**
 * Position the floating detail panel relative to its anchor, horizontally clamped to the
 * chart container. Prefers above the marker; falls back below only when there isn't enough
 * VIEWPORT room above but there is below. Vertical fit is checked against the viewport, not
 * the container: when the graph is sticky its container hugs the viewport bottom, so
 * container-relative fit would always force "below" — pushing the panel off-screen. The
 * panel may get a negative container-relative top; overflow is visible, so it renders above
 * the chart.
 */
export const computeDetailPanelPosition = (anchorRect, containerEl, {width, maxHeight}) => {
  if (!anchorRect || !containerEl) return null;

  const containerRect = containerEl.getBoundingClientRect();
  const margin = 8;

  const anchorCenterX = anchorRect.left - containerRect.left + anchorRect.width / 2;
  let left = anchorCenterX - width / 2;

  const maxLeft = containerRect.width - width - margin;
  if (left < margin) left = margin;
  if (maxLeft > margin && left > maxLeft) left = maxLeft;

  const anchorTop = anchorRect.top - containerRect.top;
  const anchorBottom = anchorRect.bottom - containerRect.top;
  let top;
  let placement;

  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const fitsAboveInViewport = anchorRect.top - maxHeight - margin >= 0;
  const fitsBelowInViewport = anchorRect.bottom + margin + maxHeight <= viewportHeight;

  if (fitsAboveInViewport || !fitsBelowInViewport) {
    top = anchorTop - maxHeight - margin;
    placement = 'above';
  } else {
    top = anchorBottom + margin;
    placement = 'below';
  }

  let arrowLeft = anchorCenterX - left;
  const arrowPadding = 16;
  arrowLeft = Math.max(arrowPadding, Math.min(width - arrowPadding, arrowLeft));

  return {left, top, placement, arrowLeft};
};
